import React, { useState, useEffect } from "react";
import {
  MdMyLocation,
  MdEdit,
  MdCheck,
  MdClose,
  MdLocationOff,
} from "react-icons/md";
import { FaMapMarkerAlt } from "react-icons/fa";
import "./AdminLocationManager.scss";

export interface AdminLocation {
  lat: number;
  lng: number;
  source: "browser" | "manual";
}

interface AdminLocationManagerProps {
  onLocationChange: (location: AdminLocation | null) => void;
  initialLocation?: AdminLocation | null;
  onClose: () => void;
}

const USE_MOCK_LOCATION = false;

const AdminLocationManager: React.FC<AdminLocationManagerProps> = ({
  onLocationChange,
  onClose,
  initialLocation = null,
}) => {
  const [currentLocation, setCurrentLocation] = useState<AdminLocation | null>(
    initialLocation
  );
  const [isEditing, setIsEditing] = useState(false);
  const [editLat, setEditLat] = useState("");
  const [editLng, setEditLng] = useState("");
  const [locationError, setLocationError] = useState<string | null>(null);
  const [isRequestingLocation, setIsRequestingLocation] = useState(false);
  const [permissionStatus, setPermissionStatus] = useState<
    "prompt" | "granted" | "denied" | "unknown"
  >("unknown");

  // Check permission status on mount
  useEffect(() => {
    checkPermissionStatus();
  }, []);

  const checkPermissionStatus = async () => {
    if (!navigator.permissions) {
      setPermissionStatus("unknown");
      return;
    }

    try {
      const result = await navigator.permissions.query({
        name: "geolocation" as PermissionName,
      });
      setPermissionStatus(result.state as "prompt" | "granted" | "denied");

      // Listen for permission changes
      result.onchange = () => {
        setPermissionStatus(result.state as "prompt" | "granted" | "denied");
      };
    } catch (error) {
      console.error("Permission check error:", error);
      setPermissionStatus("unknown");
    }
  };

  const requestBrowserLocation = () => {
    setIsRequestingLocation(true);
    setLocationError(null);

    if (!navigator.geolocation) {
      setLocationError("Geolocation is not supported by your browser");
      setIsRequestingLocation(false);
      return;
    }

    const mockLocation = {
      lat: 25.7617,
      lng: -80.1918,
      source: "browser" as const,
    };

    if (USE_MOCK_LOCATION) {
      setCurrentLocation(mockLocation);
      onLocationChange(mockLocation);
      setLocationError(null);
      setIsRequestingLocation(false);
      //   checkPermissionStatus();
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const location: AdminLocation = {
          lat: position.coords.latitude,
          lng: position.coords.longitude,
          source: "browser",
        };
        setCurrentLocation(location);
        onLocationChange(location);
        setLocationError(null);
        setIsRequestingLocation(false);
        checkPermissionStatus();
      },
      (error) => {
        let errorMessage = "Failed to get location";
        switch (error.code) {
          case error.PERMISSION_DENIED:
            errorMessage =
              "Location permission denied. Please enable it in your browser settings.";
            break;
          case error.POSITION_UNAVAILABLE:
            errorMessage = "Location information unavailable";
            break;
          case error.TIMEOUT:
            errorMessage = "Location request timed out";
            break;
        }
        setLocationError(errorMessage);
        setIsRequestingLocation(false);
        checkPermissionStatus();
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0,
      }
    );
  };

  const handleStartEdit = () => {
    setIsEditing(true);
    setEditLat(currentLocation?.lat.toString() || "");
    setEditLng(currentLocation?.lng.toString() || "");
    setLocationError(null);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditLat("");
    setEditLng("");
    setLocationError(null);
  };

  const handleSaveManualLocation = () => {
    const lat = parseFloat(editLat);
    const lng = parseFloat(editLng);

    // Validation
    if (isNaN(lat) || isNaN(lng)) {
      setLocationError("Please enter valid numbers for latitude and longitude");
      return;
    }

    if (lat < -90 || lat > 90) {
      setLocationError("Latitude must be between -90 and 90");
      return;
    }

    if (lng < -180 || lng > 180) {
      setLocationError("Longitude must be between -180 and 180");
      return;
    }

    const location: AdminLocation = {
      lat,
      lng,
      source: "manual",
    };

    setCurrentLocation(location);
    onLocationChange(location);
    setIsEditing(false);
    setLocationError(null);
  };

  const handleClearLocation = () => {
    setCurrentLocation(null);
    onLocationChange(null);
    setLocationError(null);
    setIsEditing(false);
  };

  const formatCoordinate = (value: number, isLat: boolean): string => {
    const abs = Math.abs(value);
    const direction = isLat ? (value >= 0 ? "N" : "S") : value >= 0 ? "E" : "W";
    return `${abs.toFixed(6)}° ${direction}`;
  };

  return (
    <div className="admin-location-manager">
      <div className="location-manager-header">
        <div className="location-title">
          <FaMapMarkerAlt className="location-title-icon" />
          <h3>Admin Location</h3>
        </div>
      </div>

      {!currentLocation && !isEditing && (
        <div className="location-empty-state">
          <MdLocationOff className="empty-icon" />
          <p className="empty-text">No admin location set</p>
          <p className="empty-subtext">
            Set your location to calculate distances and travel times
          </p>

          <div className="location-actions">
            <button
              className="location-btn location-btn--primary"
              onClick={requestBrowserLocation}
              disabled={isRequestingLocation || permissionStatus === "denied"}
            >
              <MdMyLocation className="btn-icon" />
              <span>
                {isRequestingLocation
                  ? "Getting Location..."
                  : permissionStatus === "denied"
                  ? "Location Access Denied"
                  : "Use Current Location"}
              </span>
            </button>

            <button
              className="location-btn location-btn--secondary"
              onClick={handleStartEdit}
            >
              <MdEdit className="btn-icon" />
              <span>Enter Manually</span>
            </button>
          </div>

          {permissionStatus === "denied" && (
            <div className="permission-help">
              <p>To use your current location, please:</p>
              <ol>
                <li>Click the location icon in your browser's address bar</li>
                <li>Allow location access for this site</li>
                <li>Click "Use Current Location" again</li>
              </ol>
            </div>
          )}
        </div>
      )}

      {currentLocation && !isEditing && (
        <div className="location-display">
          <div className="location-info">
            <div className="location-badge">
              <span className="badge-icon">
                {currentLocation.source === "browser" ? "📍" : "✏️"}
              </span>
              <span className="badge-text">
                {currentLocation.source === "browser"
                  ? "Browser Location"
                  : "Manual Location"}
              </span>
            </div>

            <div className="coordinates">
              <div className="coordinate-item">
                <span className="coordinate-label">Latitude:</span>
                <span className="coordinate-value">
                  {formatCoordinate(currentLocation.lat, true)}
                </span>
              </div>
              <div className="coordinate-item">
                <span className="coordinate-label">Longitude:</span>
                <span className="coordinate-value">
                  {formatCoordinate(currentLocation.lng, false)}
                </span>
              </div>
            </div>

            <div className="coordinate-raw">
              {currentLocation.lat.toFixed(6)}, {currentLocation.lng.toFixed(6)}
            </div>
          </div>

          <div className="location-actions">
            <button
              className="location-btn location-btn--small"
              onClick={requestBrowserLocation}
              disabled={isRequestingLocation}
              title="Update from browser"
            >
              <MdMyLocation className="btn-icon" />
              {isRequestingLocation ? "Updating..." : "Update"}
            </button>

            <button
              className="location-btn location-btn--small"
              onClick={handleStartEdit}
              title="Edit manually"
            >
              <MdEdit className="btn-icon" />
              Edit
            </button>

            <button
              className="location-btn location-btn--small location-btn--danger"
              onClick={handleClearLocation}
              title="Clear location"
            >
              <MdClose className="btn-icon" />
              Clear
            </button>

            <button
              className="location-btn location-btn--small"
              onClick={onClose}
              title="Close"
            >
              <MdClose className="btn-icon" />
              Close
            </button>
          </div>
        </div>
      )}

      {isEditing && (
        <div className="location-edit">
          <div className="edit-form">
            <div className="form-group">
              <label htmlFor="latitude" className="form-label">
                Latitude (-90 to 90)
              </label>
              <input
                id="latitude"
                type="number"
                step="any"
                className="form-input"
                value={editLat}
                onChange={(e) => setEditLat(e.target.value)}
                placeholder="e.g., 28.4638137"
              />
            </div>

            <div className="form-group">
              <label htmlFor="longitude" className="form-label">
                Longitude (-180 to 180)
              </label>
              <input
                id="longitude"
                type="number"
                step="any"
                className="form-input"
                value={editLng}
                onChange={(e) => setEditLng(e.target.value)}
                placeholder="e.g., -81.3114611"
              />
            </div>
          </div>

          <div className="edit-actions">
            <button
              className="location-btn location-btn--primary"
              onClick={handleSaveManualLocation}
            >
              <MdCheck className="btn-icon" />
              Save Location
            </button>

            <button
              className="location-btn location-btn--secondary"
              onClick={handleCancelEdit}
            >
              <MdClose className="btn-icon" />
              Cancel
            </button>
          </div>
        </div>
      )}

      {locationError && (
        <div className="location-error">
          <span className="error-icon">⚠️</span>
          <span className="error-text">{locationError}</span>
        </div>
      )}
    </div>
  );
};

export default AdminLocationManager;
